import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'sinergial.theme.mode';

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

// Espeja la lógica del script pre-boot en index.html para evitar parpadeos:
// preferencia almacenada → si no hay, el esquema del sistema.
function readStoredTheme(): ThemeMode {
  if (typeof localStorage === 'undefined') {
    return prefersDark() ? 'dark' : 'light';
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark') return 'dark';
  if (stored === 'light') return 'light';
  return prefersDark() ? 'dark' : 'light';
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly mode = signal<ThemeMode>(readStoredTheme());

  readonly currentMode = this.mode.asReadonly();

  constructor() {
    this.applyTheme(this.mode());
  }

  toggle(): void {
    this.setTheme(this.mode() === 'dark' ? 'light' : 'dark');
  }

  setTheme(mode: ThemeMode): void {
    this.mode.set(mode);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode);
    }
    this.applyTheme(mode);
  }

  private applyTheme(mode: ThemeMode): void {
    const root = this.document.documentElement;
    const isDark = mode === 'dark';

    root.classList.add('theme-switching');
    root.classList.toggle('theme-dark', isDark);
    root.classList.toggle('theme-light', !isDark);
    root.setAttribute('data-theme', mode);
    // Mantiene en sincronía el atributo usado por el script pre-boot de index.html.
    root.toggleAttribute('data-sl-dark', isDark);

    setTimeout(() => {
      root.classList.remove('theme-switching');
    }, 300);
  }
}
